import { z } from "zod";
const DOCUMENT_TYPES = [
  "drawing",
  "calculation_note",
  "report",
  "specification",
  "bill_of_quantities",
  "estimate",
  "minutes",
  "correspondence",
  "photo",
  "other"
];
const DocumentTypeSchema = z.enum(DOCUMENT_TYPES);
const DOCUMENT_TYPE_CODES = {
  drawing: "PLN",
  calculation_note: "NDC",
  report: "RAP",
  specification: "CCT",
  bill_of_quantities: "BQ",
  estimate: "EST",
  minutes: "PV",
  correspondence: "COR",
  photo: "PHO",
  other: "DIV"
};
const DISCIPLINE_CODES = {
  architecture: "ARC",
  structure: "STR",
  vrd: "VRD",
  fluides: "FLU",
  electricite: "ELE",
  topographie: "TOP",
  geotechnique: "GEO",
  opc: "OPC"
};
const PHASE_CODES = {
  esq: "ESQ",
  aps: "APS",
  apd: "APD",
  pro: "PRO",
  dce: "DCE",
  act: "ACT",
  det: "DET",
  aor_provisional: "RP",
  aor_final: "RD",
  custom: "GEN"
};
const INDEX_STYLES = ["letters", "numbers"];
const IndexStyleSchema = z.enum(INDEX_STYLES);
const NAMING_TOKENS = ["PROJECT", "PHASE", "DISC", "TYPE", "NUM", "IDX", "ZONE"];
const DEFAULT_NAMING_PATTERN = "{PROJECT}-{PHASE}-{DISC}-{TYPE}-{NUM}-{IDX}";
const DEFAULT_INDEX_STYLE = "letters";
const TOKEN_FIELDS = {
  PHASE: "phaseType",
  DISC: "discipline",
  TYPE: "docType",
  ZONE: "zone"
};
const TOKEN = /\{([A-Z]+)\}/g;
function patternTokens(pattern) {
  return [...pattern.matchAll(TOKEN)].map((m) => m[1] ?? "");
}
const NamingPatternSchema = z.string().trim().min(5).max(100).refine((p) => /^[A-Z0-9._{}-]+$/.test(p), "letters A\u2013Z, digits, - _ . and {TOKENS} only").refine(
  (p) => patternTokens(p).every((t) => NAMING_TOKENS.includes(t)),
  `tokens among ${NAMING_TOKENS.map((t) => `{${t}}`).join(" ")}`
).refine((p) => !/[{}]/.test(p.replace(TOKEN, "")), "unbalanced braces").refine((p) => p.includes("{NUM}") && p.includes("{IDX}"), "the pattern needs {NUM} and {IDX}");
function requiredFields(pattern) {
  const fields = patternTokens(pattern).map((t) => TOKEN_FIELDS[t]).filter((f) => f !== void 0);
  return [...new Set(fields)];
}
const NO_PROJECT_CODE = "ORG";
const codePart = (value) => value.normalize("NFKD").replace(/[̀-ͯ]/g, "").toUpperCase().replace(/[^A-Z0-9-]/g, "");
function generateDocumentCode(pattern, values) {
  const missing = [];
  for (const field of requiredFields(pattern)) {
    const value = values[field];
    if (value === null || value === "") missing.push(field);
  }
  if (missing.length > 0) return { ok: false, missing };
  const code = pattern.replace(TOKEN, (_m, token) => {
    switch (token) {
      case "PROJECT":
        return codePart(values.projectCode ?? NO_PROJECT_CODE) || NO_PROJECT_CODE;
      case "PHASE":
        return values.phaseType ? PHASE_CODES[values.phaseType] : "";
      case "DISC":
        return values.discipline ? DISCIPLINE_CODES[values.discipline] : "";
      case "TYPE":
        return values.docType ? DOCUMENT_TYPE_CODES[values.docType] : "";
      case "ZONE":
        return codePart(values.zone ?? "");
      case "NUM":
        return String(values.number ?? 0).padStart(3, "0");
      case "IDX":
        return values.index;
      default:
        return "";
    }
  });
  return { ok: true, code };
}
const firstRevisionIndex = (style) => style === "letters" ? "A" : "0";
function isRevisionIndex(style, index) {
  return style === "letters" ? /^[A-Z]{1,3}$/.test(index) : /^(0|[1-9][0-9]{0,3})$/.test(index);
}
function nextRevisionIndex(style, previous) {
  if (previous === null) return firstRevisionIndex(style);
  if (style === "numbers") {
    return /^\d+$/.test(previous) ? String(Number(previous) + 1) : firstRevisionIndex(style);
  }
  if (!/^[A-Z]+$/.test(previous)) return firstRevisionIndex(style);
  const letters = previous.split("");
  let i = letters.length - 1;
  while (i >= 0 && letters[i] === "Z") {
    letters[i] = "A";
    i--;
  }
  if (i < 0) return `A${letters.join("")}`;
  letters[i] = String.fromCharCode((letters[i] ?? "A").charCodeAt(0) + 1);
  return letters.join("");
}
const DocumentSettingsSchema = z.strictObject({
  namingPattern: NamingPatternSchema,
  indexStyle: IndexStyleSchema
});
const DEFAULT_DOCUMENT_SETTINGS = {
  namingPattern: DEFAULT_NAMING_PATTERN,
  indexStyle: DEFAULT_INDEX_STYLE
};
function documentSettingsOf(stored) {
  const raw = stored ?? {};
  const pattern = NamingPatternSchema.safeParse(raw.namingPattern);
  const style = IndexStyleSchema.safeParse(raw.indexStyle);
  return {
    namingPattern: pattern.success ? pattern.data : DEFAULT_NAMING_PATTERN,
    indexStyle: style.success ? style.data : DEFAULT_INDEX_STYLE
  };
}
export {
  DEFAULT_DOCUMENT_SETTINGS,
  DEFAULT_INDEX_STYLE,
  DEFAULT_NAMING_PATTERN,
  DISCIPLINE_CODES,
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_CODES,
  DocumentSettingsSchema,
  DocumentTypeSchema,
  INDEX_STYLES,
  IndexStyleSchema,
  NAMING_TOKENS,
  NO_PROJECT_CODE,
  NamingPatternSchema,
  PHASE_CODES,
  TOKEN_FIELDS,
  documentSettingsOf,
  firstRevisionIndex,
  generateDocumentCode,
  isRevisionIndex,
  nextRevisionIndex,
  patternTokens,
  requiredFields
};
//# sourceMappingURL=naming.js.map